import {
    CloseCircleOutlined,
    DeleteOutlined,
    DownOutlined,
    EllipsisOutlined,
    FilterOutlined,
    InboxOutlined,
    PlusOutlined,
    UndoOutlined,
} from '@ant-design/icons';
import { PageHeader } from '@ant-design/pro-layout';
import { useDebounce } from '@react-hook/debounce';
import {
    Badge,
    Button,
    Divider,
    Dropdown,
    Input,
    Modal,
    notification,
    Popover,
    Select,
    Switch,
    Tag,
    Tooltip,
    Typography,
} from 'antd';
import type { MenuProps } from 'antd';
import type { ColumnType, Key, SortOrder } from 'antd/lib/table/interface';
import { GenericOverviewTable } from 'components/GenericOverviewTable';
import { DestinationTravelThemeSelector } from 'components/DestinationEditor/DestinationTravelThemeSelector';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import {
    archiveDestination,
    deleteDestination,
    getAllDestinations,
    unarchiveDestination,
} from 'api/destinations.api';
import { useTravelThemes } from 'api/travel-themes.api';
import { DestinationContentSources } from 'constants/destination-content-sources';
import type { DestinationsListStateUpdate } from 'hooks/use-destinations-list-state';
import { useDestinationsListState } from 'hooks/use-destinations-list-state';
import type { DestinationSchema } from 'typings/destinations/DestinationSchema';
import type { DestinationContentTypes } from 'typings/destinations/DestinationContentTypeEnum';
import { DestinationListImage } from './DestinationListImage';
import styles from './style.module.css';
import { DestinationStatuses } from 'constants/destination-statuses';
import { DestinationGeoType } from 'constants/destination-geo-type';
import { formatCompactNumber } from 'utils/format-number';
import {
    CONTENT_TYPE_OPTIONS,
    useDestinationFilters,
} from './use-destination-filters';

const { Search } = Input;

export function DestinationsOverview() {
    const navigate = useNavigate();
    const {
        query,
        page,
        sortColumn,
        sortOrder,
        onlyWithoutPhotos,
        hasGuide,
        source,
        status,
        geoType,
        hasContent,
        contentType,
        allHighlightsHaveImages,
        returnToUrl,
        updateState,
    } = useDestinationsListState();
    const [debouncedQuery, setDebouncedQuery] = useDebounce(query, 300);

    const [isDeleting, setIsDeleting] = useState(false);
    const [isArchiving, setIsArchiving] = useState(false);
    const [tableRefreshTrigger, setTableRefreshTrigger] = useState(0);
    const { themes } = useTravelThemes();

    const addMenuItems: MenuProps['items'] = useMemo(
        () => [
            {
                key: 'place',
                label: 'Place',
                onClick: () =>
                    navigate(
                        `/destinations/add?geo_type=${DestinationGeoType.PLACE}`,
                    ),
            },
            {
                key: 'country',
                label: 'Country',
                onClick: () =>
                    navigate(
                        `/destinations/add?geo_type=${DestinationGeoType.COUNTRY}`,
                    ),
            },
        ],
        [navigate],
    );

    const filterWithEnoughCharacters =
        debouncedQuery.length > 2 ? debouncedQuery : '';

    // Memoize the API parameters to prevent unnecessary re-renders
    const apiParams = useMemo(
        () => ({
            query: filterWithEnoughCharacters,
            onlyWithoutPhotos,
            hasGuide,
            status,
            source,
            geo_type: geoType,
            sortColumn,
            sortOrder,
            has_content: hasContent,
            content_type: contentType as DestinationContentTypes | undefined,
            allHighlightsHaveImages,
        }),
        [
            filterWithEnoughCharacters,
            onlyWithoutPhotos,
            hasGuide,
            source,
            status,
            geoType,
            hasContent,
            contentType,
            allHighlightsHaveImages,
            sortColumn,
            sortOrder,
        ],
    );

    const apiGetter = useCallback(
        (pageNumber: number) =>
            getAllDestinations({
                ...apiParams,
                page: pageNumber,
            }),
        [apiParams],
    );

    const filterKey = JSON.stringify(apiParams);

    const handleUpdateRefreshTrigger = useCallback(() => {
        setTableRefreshTrigger((prev) => prev + 1);
    }, []);

    const handleDeleteDestination = useCallback(
        async (destinationId: number) => {
            setIsDeleting(true);
            try {
                await deleteDestination(destinationId);
                handleUpdateRefreshTrigger();
            } catch (error) {
                notification.error({
                    message: 'Error deleting destination',
                    description:
                        error instanceof Error
                            ? error.message
                            : 'Unknown error',
                });
            } finally {
                setIsDeleting(false);
            }
        },
        [handleUpdateRefreshTrigger],
    );

    const handleShowDeleteConfirmation = useCallback(
        (destination: DestinationSchema) => {
            Modal.confirm({
                type: 'warning',
                title: 'Delete destination?',
                content:
                    'This permanently deletes the destination and all its content. This cannot be undone. Consider archiving instead if you might need it again.',
                onOk: () => {
                    handleDeleteDestination(destination.id);
                },
                okText: 'Delete',
                okButtonProps: {
                    danger: true,
                },
                cancelText: 'Cancel',
            });
        },
        [handleDeleteDestination],
    );

    const handleArchiveDestination = useCallback(
        async (destination: DestinationSchema) => {
            setIsArchiving(true);
            try {
                await archiveDestination(destination);
                handleUpdateRefreshTrigger();
            } catch (error) {
                notification.error({
                    message: 'Error archiving destination',
                    description:
                        error instanceof Error
                            ? error.message
                            : 'Unknown error',
                });
            } finally {
                setIsArchiving(false);
            }
        },
        [handleUpdateRefreshTrigger],
    );

    const handleShowArchiveConfirmation = useCallback(
        (destination: DestinationSchema) => {
            Modal.confirm({
                type: 'warning',
                title: 'Archive destination?',
                content:
                    'The destination will be hidden from the default list, but can be restored anytime from the Archived filter.',
                onOk: () => {
                    handleArchiveDestination(destination);
                },
                okText: 'Archive',
                cancelText: 'Cancel',
            });
        },
        [handleArchiveDestination],
    );

    const handleUnarchiveDestination = useCallback(
        async (destination: DestinationSchema) => {
            setIsArchiving(true);
            try {
                await unarchiveDestination(destination);
                handleUpdateRefreshTrigger();
            } catch (error) {
                notification.error({
                    message: 'Error unarchiving destination',
                    description:
                        error instanceof Error
                            ? error.message
                            : 'Unknown error',
                });
            } finally {
                setIsArchiving(false);
            }
        },
        [handleUpdateRefreshTrigger],
    );

    const columns = useMemo<ColumnType<DestinationSchema>[]>(
        () => [
            {
                title: 'Image',
                dataIndex: 'image_url',
                key: 'cms_media_id',
                render: (_text: unknown, destination: DestinationSchema) => (
                    <DestinationListImage
                        destination={destination}
                        onUpdate={handleUpdateRefreshTrigger}
                    />
                ),
                sorter: false,
            },
            {
                title: 'Name',
                dataIndex: 'name',
                key: 'locality',
                render: (_text: unknown, destination: DestinationSchema) => (
                    <Link
                        to={`/destinations/${destination.id}?tab=basicInfo`}
                        state={{
                            returnTo: returnToUrl,
                        }}
                    >
                        {destination.name || destination.locality}
                    </Link>
                ),
                sorter: true,
                sortOrder: getGenericTableSortOrder({
                    column: 'locality',
                    currentSortColumn: sortColumn,
                    sortOrder,
                }),
            },
            {
                title: 'Country',
                dataIndex: 'country',
                key: 'country',
                render: (_text: unknown, destination: DestinationSchema) =>
                    destination.geo_type === DestinationGeoType.COUNTRY ? (
                        <span className={styles.emptyCell}> - </span>
                    ) : (
                        <Link
                            to={`/destinations/${destination.id}?tab=basicInfo`}
                            state={{
                                returnTo: returnToUrl,
                            }}
                        >
                            {destination.country}
                        </Link>
                    ),
                sorter: true,
                sortOrder: getGenericTableSortOrder({
                    column: 'country',
                    currentSortColumn: sortColumn,
                    sortOrder,
                }),
            },
            {
                title: 'Source',
                dataIndex: 'content_source',
                key: 'content_source',
                render: (_text: unknown, destination: DestinationSchema) =>
                    getContentSource(destination),
                sorter: false,
            },
            {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                render: (_text: unknown, destination: DestinationSchema) => {
                    if (destination.status === DestinationStatuses.PUBLISHED) {
                        return <Tag color="green">Published</Tag>;
                    }
                    if (destination.status === DestinationStatuses.ARCHIVED) {
                        return <Tag color="default">Archived</Tag>;
                    }
                    return <Tag color="blue">Draft</Tag>;
                },
                sorter: false,
            },
            {
                title: 'Geo type',
                dataIndex: 'geo_type',
                key: 'geo_type',
                render: (_text: unknown, destination: DestinationSchema) =>
                    destination.geo_type === DestinationGeoType.COUNTRY ? (
                        <Tag color="green">Country</Tag>
                    ) : (
                        <Tag color="blue">Place</Tag>
                    ),
                sorter: false,
            },
            {
                title: (
                    <div className={styles.visitorColumnHeader}>
                        <span>Visitors</span>
                        <span className={styles.visitorColumnHeader__period}>
                            (last year)
                        </span>
                    </div>
                ),
                dataIndex: 'visitors_t1',
                key: 'visitors_t1',
                align: 'center',
                render: (_text: unknown, destination: DestinationSchema) => (
                    <Tooltip
                        title={
                            <div className={styles.visitorTooltip}>
                                <div>
                                    <span
                                        className={styles.visitorTooltip__label}
                                    >
                                        Last year:
                                    </span>
                                    <span>
                                        {destination.visitors_t1?.toLocaleString() ??
                                            'No data'}
                                    </span>
                                </div>
                                <div>
                                    <span
                                        className={styles.visitorTooltip__label}
                                    >
                                        Last month:
                                    </span>
                                    <span>
                                        {destination.visitors_t2?.toLocaleString() ??
                                            'No data'}
                                    </span>
                                </div>
                            </div>
                        }
                    >
                        <span className={styles.visitorCount}>
                            {formatCompactNumber(destination.visitors_t1)}
                        </span>
                    </Tooltip>
                ),
                sorter: true,
                sortOrder: getGenericTableSortOrder({
                    column: 'visitors_t1',
                    currentSortColumn: sortColumn,
                    sortOrder,
                }),
            },
            {
                title: 'Themes',
                key: 'travel_themes',
                width: 140,
                render: (_text: unknown, destination: DestinationSchema) => (
                    <DestinationTravelThemeSelector
                        destinationId={destination.id}
                        allThemes={themes}
                        travelThemes={destination.travel_themes ?? []}
                        onUpdate={handleUpdateRefreshTrigger}
                    />
                ),
                sorter: false,
            },
            {
                title: 'Actions',
                dataIndex: 'actions',
                key: 'actions',
                render: (_text: unknown, destination: DestinationSchema) => {
                    const isArchived =
                        destination.status === DestinationStatuses.ARCHIVED;

                    return (
                        <Dropdown
                            trigger={['click']}
                            disabled={
                                destination.geo_type ===
                                DestinationGeoType.COUNTRY
                            }
                            menu={{
                                items: [
                                    isArchived
                                        ? {
                                              key: 'unarchive',
                                              label: 'Unarchive',
                                              icon: <UndoOutlined />,
                                              onClick: () =>
                                                  handleUnarchiveDestination(
                                                      destination,
                                                  ),
                                          }
                                        : {
                                              key: 'archive',
                                              label: 'Archive',
                                              icon: <InboxOutlined />,
                                              onClick: () =>
                                                  handleShowArchiveConfirmation(
                                                      destination,
                                                  ),
                                          },
                                    {
                                        key: 'delete',
                                        label: 'Delete',
                                        icon: <DeleteOutlined />,
                                        danger: true,
                                        onClick: () =>
                                            handleShowDeleteConfirmation(
                                                destination,
                                            ),
                                    },
                                ],
                            }}
                        >
                            <Button
                                size="middle"
                                icon={<EllipsisOutlined />}
                                loading={isDeleting || isArchiving}
                                disabled={
                                    destination.geo_type ===
                                    DestinationGeoType.COUNTRY
                                }
                            />
                        </Dropdown>
                    );
                },
                sorter: false,
            },
        ],
        [
            handleShowArchiveConfirmation,
            handleShowDeleteConfirmation,
            handleUnarchiveDestination,
            handleUpdateRefreshTrigger,
            isArchiving,
            isDeleting,
            returnToUrl,
            sortColumn,
            sortOrder,
            themes,
        ],
    );

    const handleSort = useCallback(
        (column: Key | undefined, order: SortOrder | undefined) => {
            updateState({
                sortColumn: column ? column.toString() : undefined,
                sortOrder: order ? getSortOrder(order) : undefined,
                page: 1,
            });
        },
        [updateState],
    );

    useEffect(() => {
        updateState({ query: debouncedQuery });
    }, [debouncedQuery, updateState]);

    /**
     * Add button is different in development and production.
     * In development, we use a dropdown to select the geo type.
     * In production, we use a link to the add destination page.
     * This is because in development, we want to be able to add destinations to both localities and countries.
     * In production, we want to be able to add destinations to localities only because all the countries are already in the database.
     */
    const addButton = useMemo(
        () =>
            process.env.NODE_ENV === 'development' ? (
                <Dropdown menu={{ items: addMenuItems }} trigger={['click']}>
                    <Button type="primary" size="large" icon={<PlusOutlined />}>
                        Add <DownOutlined />
                    </Button>
                </Dropdown>
            ) : (
                <Link
                    to={`/destinations/add?geo_type=${DestinationGeoType.PLACE}`}
                    className="full-page-header-tools-add-button"
                >
                    <Button type="primary" size="large" icon={<PlusOutlined />}>
                        Add
                    </Button>
                </Link>
            ),
        [addMenuItems],
    );

    return (
        <div className="full-page">
            <PageHeader ghost={false} title="Destinations" extra={addButton}>
                <DestinationFilters
                    query={query}
                    source={source}
                    status={status}
                    geoType={geoType}
                    hasContent={hasContent}
                    contentType={contentType}
                    onlyWithoutPhotos={onlyWithoutPhotos}
                    hasGuide={hasGuide}
                    allHighlightsHaveImages={allHighlightsHaveImages}
                    setDebouncedQuery={setDebouncedQuery}
                    updateState={updateState}
                />
            </PageHeader>
            <div className="full-page-content">
                <div className="max-content-width mb-m">
                    <GenericOverviewTable
                        key={`${filterKey}-${tableRefreshTrigger}`}
                        className={styles.table}
                        columns={columns}
                        apiGetter={apiGetter}
                        emptyTableMessage={
                            query
                                ? 'No destinations found with this text.'
                                : 'Sorry, no destinations yet.'
                        }
                        sortCallback={handleSort}
                        initialCurrentPage={page}
                        onPageChange={(p) => updateState({ page: p })}
                    />
                </div>
            </div>
        </div>
    );
}

type DestinationFiltersProps = Readonly<{
    query: string;
    source?: number | null;
    status?: number | null;
    geoType?: number | null;
    hasContent?: boolean;
    contentType?: number;
    onlyWithoutPhotos: boolean;
    hasGuide: boolean;
    allHighlightsHaveImages?: boolean | null;
    setDebouncedQuery: (query: string) => void;
    updateState: (state: DestinationsListStateUpdate) => void;
}>;

function DestinationFilters({
    query,
    source = null,
    status = null,
    geoType = null,
    hasContent = false,
    contentType,
    onlyWithoutPhotos,
    hasGuide,
    allHighlightsHaveImages = null,
    setDebouncedQuery,
    updateState,
}: DestinationFiltersProps) {
    const {
        searchValue,
        isMoreFiltersOpen,
        setIsMoreFiltersOpen,
        activeSecondaryFilterCount,
        hasAnyActiveFilter,
        contentTypeLabel,
        handleSearchChange,
        handleStatusChange,
        handleSourceChange,
        handleGeoTypeChange,
        handleContentTypeChange,
        handleHasContentChange,
        handleHasGuideChange,
        handleOnlyWithoutPhotosChange,
        handleAllHighlightsHaveImagesChange,
        handleClearAll,
    } = useDestinationFilters({
        query,
        source,
        status,
        geoType,
        contentType,
        onlyWithoutPhotos,
        hasGuide,
        allHighlightsHaveImages,
        setDebouncedQuery,
        updateState,
    });

    const moreFiltersContent = (
        <div className={styles.moreFilters}>
            <div className={styles.moreFilters__section}>
                <Typography.Text
                    strong
                    className={styles.moreFilters__sectionTitle}
                >
                    Content
                </Typography.Text>
                <div className={styles.filter__item}>
                    <Typography.Text type="secondary">
                        Content type:
                    </Typography.Text>
                    <Select
                        size="large"
                        value={contentType ?? null}
                        onChange={handleContentTypeChange}
                        className={styles.filter__select}
                        options={CONTENT_TYPE_OPTIONS}
                        style={{ minWidth: 140 }}
                    />
                </div>
                {contentType !== undefined && (
                    <div className={styles.filter__item}>
                        <Typography.Text type="secondary">
                            Has{' '}
                            {contentTypeLabel ? `"${contentTypeLabel}" ` : ''}
                            content:
                        </Typography.Text>
                        <Switch
                            checked={hasContent}
                            onChange={handleHasContentChange}
                        />
                    </div>
                )}
                <div className={styles.filter__item}>
                    <Typography.Text type="secondary">
                        Has guide:
                    </Typography.Text>
                    <Switch
                        checked={hasGuide}
                        onChange={handleHasGuideChange}
                    />
                </div>
            </div>
            <Divider className={styles.moreFilters__divider} />
            <div className={styles.moreFilters__section}>
                <Typography.Text
                    strong
                    className={styles.moreFilters__sectionTitle}
                >
                    Media
                </Typography.Text>
                <div className={styles.filter__item}>
                    <Typography.Text type="secondary">
                        Missing cover image:
                    </Typography.Text>
                    <Switch
                        checked={onlyWithoutPhotos}
                        onChange={handleOnlyWithoutPhotosChange}
                    />
                </div>
                <div className={styles.filter__item}>
                    <Typography.Text type="secondary">
                        Highlight images:
                    </Typography.Text>
                    <Select
                        size="large"
                        value={allHighlightsHaveImages}
                        onChange={handleAllHighlightsHaveImagesChange}
                        className={styles.filter__select}
                        style={{ minWidth: 160 }}
                    >
                        <Select.Option value={null}>Any</Select.Option>
                        <Select.Option value={true}>
                            All have images
                        </Select.Option>
                        <Select.Option value={false}>
                            Missing images
                        </Select.Option>
                    </Select>
                </div>
            </div>
            <Divider className={styles.moreFilters__divider} />
            <Button
                type="link"
                className={styles.moreFilters__clearAll}
                disabled={!hasAnyActiveFilter}
                onClick={handleClearAll}
            >
                Clear all filters
            </Button>
        </div>
    );

    return (
        <div className={styles.filters}>
            <div className={styles.filter__row}>
                <Search
                    className="full-page-header-tools-filter"
                    placeholder="Search for destinations"
                    size="large"
                    value={searchValue}
                    onChange={(e) => handleSearchChange(e.target.value)}
                />
                <Divider type="vertical" />
                <div className={styles.filter__item}>
                    <Typography.Text type="secondary">Status:</Typography.Text>
                    <Select
                        size="large"
                        value={status}
                        onChange={handleStatusChange}
                        className={styles.filter__select}
                    >
                        <Select.Option value={null}>All</Select.Option>
                        <Select.Option value={DestinationStatuses.DRAFT}>
                            Draft
                        </Select.Option>
                        <Select.Option value={DestinationStatuses.PUBLISHED}>
                            Published
                        </Select.Option>
                        <Select.Option value={DestinationStatuses.ARCHIVED}>
                            Archived
                        </Select.Option>
                    </Select>
                </div>
                <Divider type="vertical" />
                <div className={styles.filter__item}>
                    <Typography.Text type="secondary">Source:</Typography.Text>
                    <Select
                        size="large"
                        value={source}
                        onChange={handleSourceChange}
                        className={styles.filter__select}
                    >
                        <Select.Option value={null}>All</Select.Option>
                        <Select.Option value={DestinationContentSources.MANUAL}>
                            Manual
                        </Select.Option>
                        <Select.Option value={DestinationContentSources.AI}>
                            AI
                        </Select.Option>
                        <Select.Option
                            value={DestinationContentSources.FROM_GUIDES}
                        >
                            Guides
                        </Select.Option>
                    </Select>
                </div>
                <Divider type="vertical" />
                <div className={styles.filter__item}>
                    <Typography.Text type="secondary">
                        Geo type:
                    </Typography.Text>
                    <Select
                        size="large"
                        value={geoType}
                        onChange={handleGeoTypeChange}
                        className={styles.filter__select}
                    >
                        <Select.Option value={null}>All</Select.Option>
                        <Select.Option value={DestinationGeoType.PLACE}>
                            Place
                        </Select.Option>
                        <Select.Option value={DestinationGeoType.COUNTRY}>
                            Country
                        </Select.Option>
                    </Select>
                </div>
                <Divider type="vertical" />
                <Popover
                    open={isMoreFiltersOpen}
                    onOpenChange={setIsMoreFiltersOpen}
                    trigger="click"
                    placement="bottomLeft"
                    title="More filters"
                    content={moreFiltersContent}
                >
                    <Badge count={activeSecondaryFilterCount} size="small">
                        <Button size="large" icon={<FilterOutlined />}>
                            More filters
                        </Button>
                    </Badge>
                </Popover>
                {hasAnyActiveFilter && (
                    <Button
                        type="link"
                        icon={<CloseCircleOutlined />}
                        onClick={handleClearAll}
                    >
                        Clear all
                    </Button>
                )}
            </div>
        </div>
    );
}

function getSortOrder(order: SortOrder | undefined) {
    return order === 'ascend' ? 'asc' : 'desc';
}

type GetGenericTableSortOrderParams = {
    column: string;
    currentSortColumn?: string;
    sortOrder?: string;
};

function getGenericTableSortOrder({
    column,
    currentSortColumn,
    sortOrder,
}: GetGenericTableSortOrderParams) {
    if (currentSortColumn === column) {
        if (!sortOrder) {
            return undefined;
        }

        return sortOrder === 'asc' ? 'ascend' : 'descend';
    }
    return undefined;
}

function getContentSource(destination: DestinationSchema): JSX.Element {
    if (
        !destination.contents ||
        destination.contents.every(
            (content) =>
                (!content.title || content.title.trim() === '') &&
                (!content.body || content.body.trim() === ''),
        )
    ) {
        return <Tag color="gray">No content</Tag>;
    }

    const sources = destination.contents.map((content) => content.source);
    const uniqueSources = [...new Set(sources)];

    const allManualOrGuides = uniqueSources.every(
        (source) =>
            source === DestinationContentSources.MANUAL ||
            source === DestinationContentSources.FROM_GUIDES,
    );

    if (allManualOrGuides) {
        return <Tag color="orange">Manual</Tag>;
    }

    if (
        uniqueSources.length === 1 &&
        uniqueSources[0] === DestinationContentSources.AI
    ) {
        return <Tag color="green">AI</Tag>;
    }

    return <Tag color="purple">Mixed</Tag>;
}
